"use client";

import { useEffect, useRef, useCallback, useState } from "react";
import Image from "next/image";
import { useAuth } from "@/hooks/useAuth";
import { apiFetch } from "@/lib/api/client";
import type { SkillNotification } from "@/app/api/notifications/skill/route";
import Glyph from "@/components/ui/Glyph";
import type { UiTextKey } from "@/lib/ui-texts";
import { useUiTexts } from "@/lib/ui-texts-client";

const POLL_INTERVAL = 5000;
const DISPLAY_DURATION = 4500;
const TRANSITION_MS = 350;

/** 報酬を受け取った画面が、知らせをすぐ取りに行かせる合図（5秒待たずに「報酬獲得！」を出す） */
export const REWARDS_POLL_EVENT = "library:rewards-poll";
/** プレゼントボックスの受け取っていない数が分かるたびに出す合図（detail が数。ホームの🎁の右上の数） */
export const PRESENT_COUNT_EVENT = "library:present-count";

const TYPE_ICON: Record<string, string> = {
  spirit: "✨",
  material: "💎",
  hand_limit: "🖐️",
  adventure_limit: "🗺️",
  background: "🖼️",
  novel: "📖",
  present: "🎁",
  daily: "📅",
};

/** 知らせの見出し（rewardType ごと。無いものはスキルツリー）。台帳の名前 */
const HEADING: Record<string, UiTextKey> = {
  present: "quests.unlockToast.present",
  daily: "quests.unlockToast.daily",
};

export default function SkillUnlockToast() {
  const { user, isLoggedIn } = useAuth();
  const { t } = useUiTexts();
  const [current, setCurrent] = useState<SkillNotification | null>(null);
  const [visible, setVisible] = useState(false);
  const queueRef = useRef<SkillNotification[]>([]);
  const isShowingRef = useRef(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nextTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const showNext = useCallback(() => {
    if (queueRef.current.length === 0) {
      isShowingRef.current = false;
      setCurrent(null);
      return;
    }
    const next = queueRef.current.shift()!;
    setCurrent(next);
    // 少し遅らせてトランジションを発火
    nextTimerRef.current = setTimeout(() => setVisible(true), 30);

    hideTimerRef.current = setTimeout(() => {
      setVisible(false);
      nextTimerRef.current = setTimeout(showNext, TRANSITION_MS + 50);
    }, DISPLAY_DURATION);
  }, []);

  const enqueue = useCallback(
    (notifications: SkillNotification[]) => {
      if (notifications.length === 0) return;
      queueRef.current.push(...notifications);
      if (!isShowingRef.current) {
        isShowingRef.current = true;
        showNext();
      }
    },
    [showNext],
  );

  const poll = useCallback(async () => {
    if (!user) return;
    try {
      const token = await user.getIdToken();
      const res = await apiFetch<{ notifications: SkillNotification[]; presentCount?: number | null }>(
        "/api/notifications/skill",
        token,
      );
      if (res.notifications?.length > 0) enqueue(res.notifications);
      if (typeof res.presentCount === "number") window.dispatchEvent(new CustomEvent(PRESENT_COUNT_EVENT, { detail: res.presentCount }));
    } catch {
      // 通知取得失敗は無視
    }
  }, [user, enqueue]);

  useEffect(() => {
    if (!isLoggedIn || !user) return;
    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL);
    const pollNow = () => { poll(); };
    window.addEventListener(REWARDS_POLL_EVENT, pollNow);
    return () => {
      window.removeEventListener(REWARDS_POLL_EVENT, pollNow);
      if (pollRef.current) clearInterval(pollRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      if (nextTimerRef.current) clearTimeout(nextTimerRef.current);
    };
  }, [isLoggedIn, user, poll]);

  if (!current) return null;

  // 報酬獲得の知らせは、絵があればどの種類でも出す（素材・精霊）。無ければ贈り物の印
  const isReward = current.kind === "reward";
  const hasImage =
    current.imageUrl &&
    (isReward || current.rewardType === "spirit" || current.rewardType === "material");

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-[200] flex justify-center pointer-events-none transition-all duration-[350ms] ease-out ${
        visible ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0"
      }`}
    >
      <div className="mt-3 mx-4 pointer-events-auto">
        <div className={`flex items-center gap-3 bg-gray-950/95 backdrop-blur-md border rounded-2xl shadow-2xl px-4 py-3 min-w-[260px] max-w-xs ${isReward ? "border-amber-400/60 shadow-amber-900/40" : "border-violet-500/50 shadow-violet-900/40"}`}>
          {/* 画像 or アイコン */}
          {hasImage ? (
            <div className="relative w-[72px] h-[72px] shrink-0 rounded-xl overflow-hidden bg-black/30">
              {current.avatarType === "video" ? (
                <video
                  src={current.imageUrl}
                  className="w-full h-full object-contain"
                  muted
                  loop
                  autoPlay
                  playsInline
                />
              ) : (
                <Image
                  src={current.imageUrl}
                  alt={current.nodeName}
                  fill
                  className="object-contain"
                  sizes="72px"
                />
              )}
            </div>
          ) : (
            <div className="w-[72px] h-[72px] shrink-0 rounded-xl bg-violet-900/40 border border-violet-500/30 flex items-center justify-center text-2xl">
              <Glyph e={isReward ? "🎁" : TYPE_ICON[current.rewardType] ?? "🎯"} />
            </div>
          )}

          {/* テキスト */}
          <div className="flex-1 min-w-0">
            <p className={`mb-0.5 font-medium ${isReward ? "text-xs font-bold text-amber-300" : "text-[10px] text-violet-400"}`}>
              {isReward ? t("quests.rewardToast.heading") : t(HEADING[current.rewardType] ?? "quests.unlockToast.skill")}
            </p>
            <p className="text-sm font-bold text-white leading-snug">{current.label}</p>
            {/* 報酬獲得の知らせは、手に入れたものの入った先（プレゼントボックス・素材の持ち物…）を文で */}
            {isReward && (current.destinations ?? []).length > 0 && (
              <p className="mt-0.5 text-[11px] leading-snug text-emerald-300">
                {(current.destinations ?? []).map((d) => t(`quests.rewardToast.dest.${d}` as UiTextKey)).join(" / ")}
              </p>
            )}
            {current.nodeName && (
              <p className="text-[10px] text-gray-400 mt-0.5 truncate">{current.nodeName}</p>
            )}
          </div>

          {/* 閉じるボタン */}
          <button
            onClick={() => {
              if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
              setVisible(false);
              nextTimerRef.current = setTimeout(showNext, TRANSITION_MS + 50);
            }}
            className="shrink-0 text-gray-500 hover:text-gray-300 transition-colors text-lg leading-none ml-1"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}
